import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Wrench } from '@phosphor-icons/react';
import { useBranding } from '../context/BrandingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pickLang = (obj, lang) => {
  if (!obj) return '';
  return obj[lang] || obj.fr || obj.en || obj.mg || '';
};

/* Full-screen fallback shown by MaintenanceGate for anyone who isn't an
   admin while platform_config.maintenance_mode is true. Content comes from
   the "maintenance" default page (GET /pages/maintenance) so it stays
   editable from Admin > Pages, with a hardcoded FR string as a last-resort
   fallback if that request itself fails during the outage. */
export const MaintenancePage = () => {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const [page, setPage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pages/maintenance`, { params: { lang: i18n.language || 'fr' } })
      .then((res) => setPage(res.data))
      .catch(() => setPage(null));
  }, [i18n.language]);

  const lang = i18n.language || 'fr';
  const title = page ? pickLang(page.title, lang) : 'Site en maintenance';
  const html = page
    ? pickLang(page.content, lang)
    : '<p>Nous améliorons ATÔ pour vous offrir une meilleure expérience. Nous serons de retour très bientôt.</p>';
  const sanitized = useMemo(() => DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }), [html]);

  const logoUrl = branding?.logo_path
    ? (branding.logo_path.startsWith('http') ? branding.logo_path : `${API}/files/${branding.logo_path}`)
    : null;
  const platformName = branding?.platform_name || 'ATÔ';

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-6 text-center" data-testid="maintenance-page">
      <div className="max-w-md w-full">
        {logoUrl ? (
          <img src={logoUrl} alt={platformName} className="h-10 w-auto object-contain mx-auto mb-8" data-testid="maintenance-logo" />
        ) : (
          <span className="text-2xl font-black font-heading text-text_strong mb-8 block" data-testid="maintenance-logo">
            {platformName}
          </span>
        )}

        <div className="w-16 h-16 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center mx-auto mb-6">
          <Wrench size={28} weight="bold" className="text-primary" />
        </div>

        <h1 className="text-2xl font-bold text-text_strong mb-3" data-testid="maintenance-title">
          {title}
        </h1>

        <div className="text-base text-text_muted leading-relaxed" data-testid="maintenance-message">
          {parse(sanitized)}
        </div>

        <p className="mt-12 text-sm text-text_muted">
          {t('maintenance.adminPrompt')}{' '}
          <Link to="/login" className="text-primary hover:underline font-medium" data-testid="maintenance-admin-login-link">
            {t('maintenance.loginLink')}
          </Link>
        </p>
      </div>
    </div>
  );
};
